import {
  CountryCode,
  ParseError,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js/max';

/**
 * @description Regex to parse simple US phone numbers with no area code, ignoring
 * starting and ending whitespace
 * works with simple formatting or all numbers
 */
export const US7DigitPhoneRegex = /^\s*([2-9][0-9]{2})[- ]*([0-9]{4})\s*$/; // works with simple formatting or all numbers

export const sanitizePhoneNumber = (phoneNumber: string | undefined) =>
  (phoneNumber ?? '').trim().replaceAll('_', '-'); // underscores cause trivial errors, assume they are hyphens

export const isEmptyPhoneNumber = (phoneNumber: string | undefined) => {
  const cleanNumber = sanitizePhoneNumber(phoneNumber);

  return !phoneNumber?.length || !cleanNumber.length;
};

/**
 * tests for a non-null, syntactically correct phone number for a given list of countries
 * @param phoneNumber
 * @param allowedCountries array of country codes that the number MUST match, if empty, any country is allowed
 * @param allow7Digit whether to allow 7 digit US phone number
 * @return FALSE if the input is (effectively) null or empty,
 *  T
 *  if it's an international (+1 style) and rational length,
 *  if it is US style, it must be either 7 or 10 digits and have a reasonably valid area code
 * @see https://en.wikipedia.org/wiki/North_American_Numbering_Plan
 */
export const validatePhoneNumber = (
  phoneNumber: string | undefined,
  allowedCountries = [] as CountryCode[],
  allow7Digit = false
) => {
  if (isEmptyPhoneNumber(phoneNumber)) {
    return false;
  }
  const cleanNumber = sanitizePhoneNumber(phoneNumber);

  // avoids errors from parsePhoneNumberWithError while entering a number
  // if it's too short to be valid, reject it
  // minimum length is 7 digits for US local, 10 for US full, 8 for UK local (011 44), 11 for UK full (0 44)
  // maximum length is 15 digits for international numbers
  if (cleanNumber.length < 7 || cleanNumber.length > 15) {
    return false;
  }

  const parsedPhone = parsePhoneNumberWithError(cleanNumber, 'US');
  const countries = parsedPhone.getPossibleCountries();
  const isValidCountry =
    allowedCountries?.length === 0 ||
    countries.some((country) => allowedCountries.includes(country));
  const isValid = isValidPhoneNumber(cleanNumber, { defaultCountry: 'US' });
  const isSimple = allow7Digit && !!cleanNumber.match(US7DigitPhoneRegex); // removed for eZE-1480

  return (isValid || isSimple) && isValidCountry;
};

/**
 * validate and format a phone number into either an international or US style
 * @param phoneNumber the original phone string
 * @param ignoreErrors if false, any parsing error gets thrown
 * @return a formatted US or interntational phone number, the original phoneNumber, or throws an error
 * @see https://en.wikipedia.org/wiki/North_American_Numbering_Plan
 */
export const formatPhoneNumber = (
  phoneNumber: string | undefined,
  ignoreErrors = true
): string => {
  if (!phoneNumber || !phoneNumber?.trim().length) {
    return '';
  }

  const cleanNumber = phoneNumber.trim().replaceAll('_', '-'); // underscores cause trivial errors, assume they are hyphens

  const simple = cleanNumber.match(US7DigitPhoneRegex);
  const isValid = isValidPhoneNumber(cleanNumber, { defaultCountry: 'US' });

  /** TODO: Refactor phone formatting logic to handle partial input better
   * Current issue: When typing 7 digits (e.g., "8675309"), the formatPhoneNumber function
   * should format it as "867-5309" but the validation flow prevents proper masking.
   * The isValidPhoneNumber check is too strict for partial numbers, causing the 7-digit
   * formatting logic to be bypassed. Need to implement progressive formatting that handles:
   * - 3 digits: "867"
   * - 7 digits: "867-5309"
   * - 10 digits: "(867) 530-9000"
   * This will provide better UX with real-time masking as user types.
   */
  if (simple?.length === 3) {
    return `${simple[1]}-${simple[2]}`;
  }

  if (!isValid) {
    return cleanNumber; // return but clean up spaces
  }

  // if it looks like it has a country code, don't assume US and keep international formatting
  try {
    const parsedPhone = parsePhoneNumberWithError(cleanNumber, 'US');
    const countries = parsedPhone.getPossibleCountries();

    // even though we defaulted the country to US, if it's valid for another country, use international format
    const isUSFormat = countries.some((code) => code === 'US');

    return parsedPhone.format(isUSFormat ? 'NATIONAL' : 'INTERNATIONAL', {
      humanReadable: true,
    });
  } catch (error) {
    if (ignoreErrors && error instanceof ParseError) {
      //ignore parse errors and just return the original garbage
      return cleanNumber.trim();
    } else {
      throw error;
    }
  }
};

export default formatPhoneNumber;
